import { useState, useEffect, useCallback } from 'react';
import { useGLTF } from '@react-three/drei';
import { digitalTwinApi } from '../../../Restclient/datacollection';

const LAST_MODEL_KEY = 'dt_last_model_id';

// Module-level cache — survives tab switches (component unmount/remount) but
// clears on full page reload. Means the model list is shown instantly on every
// re-visit while a background refresh quietly syncs any changes.
let _modelsCache = null;

/**
 * Call this once after the user logs in (e.g. from Homepage) to warm the
 * cache before the user ever opens the Digital Twin tab.  Errors are silently
 * swallowed — this is a best-effort prefetch only.
 */
export async function primeModelsCache() {
  try {
    const res = await digitalTwinApi.getModels();
    _modelsCache = res.data;
  } catch (_) {
    // Ignore — cache stays null, the hook will fetch normally on first open.
  }
}

export function useModelFiles() {
  // Pre-populate from cache so the first render already has data.
  const [models, setModels] = useState(() => _modelsCache || []);
  // Only block with a spinner on the very first load (no cache yet).
  const [loading, setLoading] = useState(!_modelsCache);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  /** Resolved read SAS URL for the selected model (async from API). */
  const [modelUrl, setModelUrl] = useState(null);
  const [selectedModelId, setSelectedModelId] = useState(
    () => localStorage.getItem(LAST_MODEL_KEY) || null
  );

  // background=true → stale-while-revalidate (no spinner, updates list silently)
  // background=false → blocking fetch (spinner shown, used on first load)
  const fetchModels = useCallback(async (background = false) => {
    try {
      if (!background) setLoading(true);
      const res = await digitalTwinApi.getModels();
      _modelsCache = res.data;
      setModels(res.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load models');
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we already have cached data, refresh silently in the background so
    // the user sees the list immediately. On the first ever load there is no
    // cache so we do a normal blocking fetch.
    fetchModels(!!_modelsCache);
  }, [fetchModels]);

  const selectModel = useCallback((modelId) => {
    setSelectedModelId(modelId);
    if (modelId) {
      localStorage.setItem(LAST_MODEL_KEY, modelId);
    } else {
      localStorage.removeItem(LAST_MODEL_KEY);
    }
  }, []);

  const uploadModel = useCallback(
    async (file, displayName) => {
      try {
        setUploading(true);
        setError(null);
        const formData = new FormData();
        formData.append('model', file);
        if (displayName) formData.append('displayName', displayName);
        const res = await digitalTwinApi.uploadModel(formData);
        _modelsCache = null;
        await fetchModels(false);
        return res.data;
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Upload failed');
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [fetchModels]
  );

  const deleteModel = useCallback(
    async (id) => {
      try {
        setDeleting(true);
        await digitalTwinApi.deleteModel(id);
        if (selectedModelId === id) {
          setSelectedModelId(null);
          localStorage.removeItem(LAST_MODEL_KEY);
        }
        // Invalidate cache then refresh.
        _modelsCache = null;
        await fetchModels(false);
      } finally {
        setDeleting(false);
      }
    },
    [fetchModels, selectedModelId]
  );

  const selectedModel = models.find((m) => m.id === selectedModelId) || null;

  useEffect(() => {
    let cancelled = false;
    if (!selectedModelId) {
      setModelUrl(null);
      return undefined;
    }
    setModelUrl(null);
    (async () => {
      try {
        const url = await digitalTwinApi.getModelDownloadUrl(selectedModelId);
        if (!cancelled) {
          setModelUrl(url);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setModelUrl(null);
          setError(
            err.response?.data?.error?.toString?.() ||
              err.response?.data?.error ||
              err.message ||
              'Failed to load model URL'
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedModelId]);

  // Start downloading the GLB as soon as the SAS URL is known — before the Canvas
  // mounts — so the file is already (partially) cached by the time useGLTF is
  // called inside Model.js.
  useEffect(() => {
    if (modelUrl) useGLTF.preload(modelUrl);
  }, [modelUrl]);

  return {
    models,
    loading,
    deleting,
    uploading,
    error,
    selectedModel,
    selectedModelId,
    modelUrl,
    selectModel,
    uploadModel,
    deleteModel,
    refreshModels: fetchModels,
  };
}
